/**
 * Binary-mask morphology — the replacement for cv2.dilate / erode /
 * morphologyEx(CLOSE|OPEN). Masks are single-channel {@link Mask} (0 or 255).
 *
 * OpenCV uses a rectangular structuring element of ones((k,k)); we do the same
 * with a separable box pass (dilate = max over the k-window, erode = min), which
 * is exact for a rectangular kernel. CLOSE = dilate→erode, OPEN = erode→dilate.
 */
import { Mask, createMask } from './image';

function dilate1D(src: Uint8Array, W: number, H: number, radius: number, horizontal: boolean): Uint8Array {
  const out = new Uint8Array(W * H);
  if (horizontal) {
    for (let y = 0; y < H; y++) {
      const row = y * W;
      for (let x = 0; x < W; x++) {
        let m = 0;
        const x0 = Math.max(0, x - radius);
        const x1 = Math.min(W - 1, x + radius);
        for (let xx = x0; xx <= x1; xx++) if (src[row + xx] > m) m = src[row + xx];
        out[row + x] = m;
      }
    }
  } else {
    for (let x = 0; x < W; x++) {
      for (let y = 0; y < H; y++) {
        let m = 0;
        const y0 = Math.max(0, y - radius);
        const y1 = Math.min(H - 1, y + radius);
        for (let yy = y0; yy <= y1; yy++) if (src[yy * W + x] > m) m = src[yy * W + x];
        out[y * W + x] = m;
      }
    }
  }
  return out;
}

function erode1D(src: Uint8Array, W: number, H: number, radius: number, horizontal: boolean): Uint8Array {
  const out = new Uint8Array(W * H);
  if (horizontal) {
    for (let y = 0; y < H; y++) {
      const row = y * W;
      for (let x = 0; x < W; x++) {
        let m = 255;
        const x0 = Math.max(0, x - radius);
        const x1 = Math.min(W - 1, x + radius);
        for (let xx = x0; xx <= x1; xx++) if (src[row + xx] < m) m = src[row + xx];
        out[row + x] = m;
      }
    }
  } else {
    for (let x = 0; x < W; x++) {
      for (let y = 0; y < H; y++) {
        let m = 255;
        const y0 = Math.max(0, y - radius);
        const y1 = Math.min(H - 1, y + radius);
        for (let yy = y0; yy <= y1; yy++) if (src[yy * W + x] < m) m = src[yy * W + x];
        out[y * W + x] = m;
      }
    }
  }
  return out;
}

/** Dilate with a (kernel x kernel) rectangular SE, `iterations` times. */
export function dilate(mask: Mask, kernel: number, iterations = 1): Mask {
  const r = (kernel - 1) >> 1;
  let d = mask.data;
  for (let i = 0; i < iterations; i++) {
    d = dilate1D(d, mask.width, mask.height, r, true);
    d = dilate1D(d, mask.width, mask.height, r, false);
  }
  return { data: d, width: mask.width, height: mask.height };
}

export function erode(mask: Mask, kernel: number, iterations = 1): Mask {
  const r = (kernel - 1) >> 1;
  let d = mask.data;
  for (let i = 0; i < iterations; i++) {
    d = erode1D(d, mask.width, mask.height, r, true);
    d = erode1D(d, mask.width, mask.height, r, false);
  }
  return { data: d, width: mask.width, height: mask.height };
}

/** morphologyEx CLOSE: dilate then erode. */
export function close(mask: Mask, kernel: number): Mask {
  return erode(dilate(mask, kernel), kernel);
}

/** morphologyEx OPEN: erode then dilate. */
export function open(mask: Mask, kernel: number): Mask {
  return dilate(erode(mask, kernel), kernel);
}

export { createMask };
